import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ImageGeneratorDesignSheet,
  ImageGeneratorGenerateResult,
  ImageGeneratorPatternChoice,
} from "../../app/api/imageGenerator";
import {
  categoryDisplayLabel,
  influenceDisplayLabel,
  isGenericSurfaceLabel,
  trendDisplayName,
} from "../../constants/imageGeneratorDictionaries";
import useImageGeneratorPreviews from "./useImageGeneratorPreviews";

export type ImageGeneratorResultDetails = {
  genre: string;
  season: string;
  market: string;
  category: string;
  outputType: "Style" | "Pattern";
  productType?: string;
  influence?: string;
  styleType?: string;
  material?: string;
  sleeve?: string;
  neckline?: string;
  fit?: string;
  wash?: string;
  length?: string;
  startingDirection?: "macro-theme" | "denim" | "";
  denimMaterialId?: string;
  denimWashId?: string;
  denimMaterial?: string;
  denimWash?: string;
  macroTheme?: string;
  colourStory?: string;
  patternChoice: ImageGeneratorPatternChoice;
  surfaceId?: string;
  /**
   * Selected surface slide name when microTheme is missing, a numeric id,
   * or the generic word "Surface". Preference hover does not read this.
   */
  surfaceName?: string;
  microTheme?: string;
  garmentFormat?: string;
  embroideryType?: string;
  embroideryPlacement?: string;
  patternRepeat?: string;
  patternFollowUp?: string;
  patternFollowUpText?: string;
  patternOffset?: string;
  patternDensity?: string;
  patternScale?: string;
  patternColorCount?: string;
  patternProcess?: string;
  additionalPrompt?: string;
  paletteHexes: string[];
  paletteLabels?: string[];
  /** Pantone codes aligned to paletteHexes (null when missing). */
  palettePantones?: Array<string | null>;
  preferences: Array<{ stem: string; url: string }>;
  errorMessage?: string;
  errorCode?: string;
  /** When false, Retry is hidden. Omitted/true → retryable (incl. older failed rows). */
  retryable?: boolean;
  designSheet?: ImageGeneratorDesignSheet;
};

export type ImageGeneratorResultItem = {
  id: string;
  createdAt: string;
  result: ImageGeneratorGenerateResult;
  details: ImageGeneratorResultDetails;
};

const patternChoiceLabel = (
  choice: ImageGeneratorPatternChoice | undefined,
  hasSurface: boolean
): string => {
  if (hasSurface) {
    return "Surface Trend";
  }
  switch (choice) {
    case "choose":
      return "Choose pattern";
    case "recommend":
      return "Recommend pattern";
    case "none":
      return "No pattern tile";
    default:
      return "";
  }
};

const paletteSwatchLabel = (
  details: ImageGeneratorResultDetails,
  index: number,
  hex: string
): string => {
  const pantone = (details.palettePantones || [])[index]?.trim();
  if (pantone) {
    return pantone;
  }
  const label = (details.paletteLabels || [])[index]?.trim();
  if (label) {
    return label;
  }
  return hex.toUpperCase();
};

/** Pantone (19-4305) or hex. Those are swatch codes, not a palette name. */
const isPaletteColorCode = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (/^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(trimmed)) {
    return true;
  }
  return /^\d{2}-\d{4}(?:\s*[a-z]{2,4})?$/i.test(trimmed);
};

/**
 * One palette name for the Color Palette group.
 * Colour story title first, then a shared non-code palette label.
 * Falls back to the previous swatch label so the caption is not blank.
 */
const paletteGroupLabel = (details: ImageGeneratorResultDetails): string => {
  const story = (details.colourStory || "").trim();
  if (story && !isPaletteColorCode(story)) {
    return story;
  }
  const named = Array.from(
    new Set(
      (details.paletteLabels || [])
        .map(label => (label || "").trim())
        .filter(label => label && !isPaletteColorCode(label))
    )
  );
  if (named.length === 1) {
    return named[0];
  }
  return paletteSwatchLabel(details, 0, details.paletteHexes[0] || "");
};

/** Slide title for the Surface row. Generic labels and bare ids are skipped. */
const surfaceTrendName = (value?: string | null): string => {
  const name = trendDisplayName(value);
  if (!name || isGenericSurfaceLabel(name)) {
    return "";
  }
  return name;
};

/**
 * Surface row: selected slide name (same role as Micro-theme on the pattern path).
 * If nothing stored is a real title, keep the current value so the row is not blank.
 */
const surfaceFieldValue = (details: ImageGeneratorResultDetails): string => {
  const name =
    surfaceTrendName(details.microTheme) ||
    surfaceTrendName(details.surfaceName);
  if (name) {
    return name;
  }
  const micro = (details.microTheme || "").trim();
  if (micro) {
    return micro;
  }
  return (details.surfaceId || "").trim();
};

/**
 * Preference hover. Pattern tiles use the micro-theme on the snapshot.
 * Surface slides use that slide's name when it is a real name; otherwise the id.
 */
const preferenceHoverLabel = (
  assetStem: string,
  details: ImageGeneratorResultDetails
): string => {
  const id = String(assetStem || "").trim();
  const hasSurface = Boolean((details.surfaceId || "").trim());
  if (hasSurface) {
    const slideName =
      trendDisplayName(details.microTheme) ||
      trendDisplayName(details.surfaceId);
    if (slideName && slideName !== id) {
      return slideName;
    }
    return id;
  }
  const micro = trendDisplayName(details.microTheme);
  if (micro) {
    return micro;
  }
  return id;
};

const buildImageGeneratorDetailRows = (
  details: ImageGeneratorResultDetails
): Array<[string, string]> => {
  const hasSurface = Boolean((details.surfaceId || "").trim());
  const rows: Array<[string, string]> = [
    ["Genre", details.genre],
    ["Season", details.season],
    ["Market", details.market],
    ["Category", categoryDisplayLabel(details.category)],
    ["Output", details.outputType],
  ];
  if (details.outputType === "Style") {
    rows.push(
      ["Product", details.productType || ""],
      ["Influence", influenceDisplayLabel(details.influence)],
      ["Style", details.styleType || ""],
      ["Material", details.material || ""],
      ["Sleeve", details.sleeve || ""],
      ["Neckline", details.neckline || ""],
      ["Length", details.length || ""],
      ["Fit", details.fit || ""],
      ["Wash", details.wash || ""],
      [
        "Starting",
        details.startingDirection === "denim"
          ? "Denim"
          : details.startingDirection === "macro-theme"
            ? "Macro Theme"
            : "",
      ],
      ["Denim material", details.denimMaterial || ""],
      ["Denim wash", details.denimWash || ""]
    );
  }
  rows.push(
    ["Macro Theme", details.macroTheme || ""],
    ["Colour story", details.colourStory || ""],
    [
      "Trend",
      details.startingDirection === "denim"
        ? ""
        : hasSurface
          ? "Surface"
          : details.patternChoice
            ? "Pattern"
            : "",
    ],
    ["Pattern pick", patternChoiceLabel(details.patternChoice, hasSurface)],
    ["Micro-theme", hasSurface ? "" : details.microTheme || ""],
    ["Surface", hasSurface ? surfaceFieldValue(details) : ""],
    ["Garments", details.garmentFormat || ""],
    ["Pattern format", details.patternProcess || ""],
    ["Repeat", details.patternRepeat || ""],
    ["Details", details.patternFollowUp || ""],
    ["Notes", details.patternFollowUpText || ""],
    ["Offset", details.patternOffset || ""],
    ["Print density", details.patternDensity || ""],
    ["Scale", details.patternScale || ""],
    ["Max colors", details.patternColorCount || ""],
    ["Embroidery", details.embroideryType || ""],
    ["Direction", details.additionalPrompt || ""]
  );
  return rows.filter(([, value]) => Boolean(value));
};

type Props = {
  details: ImageGeneratorResultDetails;
};

const ImageGeneratorDesignDetails: React.FC<Props> = ({ details }) => {
  const [lightbox, setLightbox] = useState<{
    src: string;
    label: string;
  } | null>(null);
  const detailRows = useMemo(
    () => buildImageGeneratorDetailRows(details),
    [details]
  );
  const preferences = details.preferences || [];
  const showPreferences = preferences.length > 0;
  const resolvedPreferences = useMemo(
    () =>
      preferences.map(asset => {
        const stem = asset.stem || "";
        const rawUrl =
          asset.url || (asset as { imageUrl?: string }).imageUrl || "";
        const normalized = stem
          .trim()
          .toLowerCase()
          .replace(/\.(png|jpe?g|webp|gif|svg)$/i, "");
        let url = rawUrl;
        if (normalized.startsWith("material-")) {
          url = `image-generator/denim-assets/materials/${normalized.replace(
            /^material-/,
            ""
          )}`;
        } else if (normalized.startsWith("wash-")) {
          url = `image-generator/denim-assets/washes/${normalized.replace(
            /^wash-/,
            ""
          )}`;
        } else if (/^\d+$/.test(stem.trim()) && !url) {
          url = `image-generator/assets/${encodeURIComponent(stem.trim())}`;
        } else if (
          !url ||
          url.includes("/assets/material-") ||
          url.includes("/assets/wash-")
        ) {
          url = rawUrl;
        }
        return { stem, url };
      }),
    [preferences]
  );
  const preferencePreviewItems = useMemo(
    () =>
      showPreferences
        ? resolvedPreferences.map((asset, index) => ({
            id: `pref-${index}-${asset.stem}`,
            imageUrl: asset.url || null,
          }))
        : [],
    [resolvedPreferences, showPreferences]
  );
  const preferencePreviews = useImageGeneratorPreviews(preferencePreviewItems);
  const paletteName =
    details.paletteHexes.length > 0 ? paletteGroupLabel(details) : "";

  return (
    <>
      <aside className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-2xl border border-[#D0D0D0] bg-white shadow-sm">
        <h3 className="shrink-0 px-2.5 pt-2.5 font-semibold text-secondary sm:px-3 sm:pt-3">
          Design Details
        </h3>
        <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2.5 pt-2 sm:px-3 sm:pb-3">
          <dl className="space-y-2">
            {detailRows.map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[minmax(4rem,5.5rem)_minmax(0,1fr)] gap-1.5 text-xs"
              >
                <dt className="font-medium text-secondary">{label}</dt>
                <dd className="break-words text-secondary" title={value}>
                  {value}
                </dd>
              </div>
            ))}
          </dl>

          {details.paletteHexes.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-sm font-medium text-secondary">
                Color Palette
              </p>
              <div className="flex w-fit min-w-0 max-w-full flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  {details.paletteHexes.map((hex, index) => (
                    <span
                      key={`${hex}-${index}`}
                      className="h-8 w-8 shrink-0 rounded-full border border-[#D0D0D0]"
                      style={{ backgroundColor: hex }}
                      title={paletteName}
                      aria-label={paletteName}
                    />
                  ))}
                </div>
                {paletteName ? (
                  <p
                    className="mt-1 max-w-full break-words text-[10px] leading-tight text-secondary"
                    title={paletteName}
                  >
                    {paletteName}
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {showPreferences ? (
            <div className="mt-5">
              <p className="mb-2 text-sm font-medium text-secondary">
                {details.surfaceId
                  ? "Surface"
                  : details.startingDirection === "denim"
                    ? "Denim refs"
                    : "Pattern tile"}
              </p>
              <div className="flex flex-wrap items-end gap-2">
                {resolvedPreferences.map((asset, index) => {
                  const previewId = `pref-${index}-${asset.stem}`;
                  const hoverLabel = preferenceHoverLabel(asset.stem, details);
                  const rawUrl = asset.url || "";
                  const previewSrc =
                    preferencePreviews[previewId] ||
                    (rawUrl &&
                    (rawUrl.startsWith("http://") ||
                      rawUrl.startsWith("https://") ||
                      rawUrl.startsWith("data:") ||
                      rawUrl.startsWith("blob:"))
                      ? rawUrl
                      : undefined);
                  return (
                    <button
                      key={previewId}
                      type="button"
                      onClick={() => {
                        if (!previewSrc) {
                          return;
                        }
                        setLightbox({ src: previewSrc, label: asset.stem });
                      }}
                      className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-[#737373]/50 bg-gray-100 outline-none transition hover:border-[#737373] focus-visible:outline-none disabled:cursor-default"
                      title={hoverLabel}
                      aria-label={hoverLabel}
                      disabled={!previewSrc}
                    >
                      {previewSrc ? (
                        <img
                          src={previewSrc}
                          alt={asset.stem}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center px-0.5 text-center text-[8px] leading-tight text-gray-500">
                          {asset.stem}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </aside>

      {lightbox
        ? createPortal(
            <div
              className="fixed inset-0 z-[210] flex items-center justify-center bg-[#1F1F1F]/70 p-4 backdrop-blur-[2px]"
              role="dialog"
              aria-modal="true"
              aria-label="Preference preview"
              onClick={() => setLightbox(null)}
            >
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary shadow"
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6l12 12M18 6 6 18"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <img
                src={lightbox.src}
                alt={lightbox.label}
                className="max-h-[90vh] max-w-[min(96vw,1400px)] rounded-2xl object-contain shadow-2xl"
                onClick={event => event.stopPropagation()}
              />
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default ImageGeneratorDesignDetails;
